import { logError, logWarning } from '../utilities/logging';
import { baseProductConfigurationSlotId } from '../utilities/strings';
import { addImageUrls } from './addImageUrls';
import { getCart } from './getCart';
import { buildPersonalisationChoices } from './buildPersonalisationChoices';
import { createPayload } from './createPayload';
import { add } from './add';
import { createImageSwitchPayload } from './createImageSwitchPayload';
import { validateChangeCartData } from '../utilities/atcViaFromDataValidation';
import { validateAndTryAgain } from './validateAndTryAgain';
import { remove } from './remove';
import { wait } from '../utilities/wait';

/**
 * @typedef {Object} ComponentGroup
 * @property {string} componentGroupId - The component group identifier
 * @property {string} variantId - The variant identifier
 * @property {string} slotId - The slot identifier
 * @property {number} [index] - Optional index for ordering
 */

/**
 * @typedef {Object} ChoicePayload
 * @property {string | null} componentGroupId - The component group identifier
 * @property {string | null} variantId - The variant identifier
 * @property {string} slotId - The slot identifier
 * @property {string} stepId - The step identifier
 * @property {number} index - Optional index for ordering
 * @property {string=} lineItemPropertyKey - The line item property key
 * @property {string=} lineItemPropertyKeyValue - The line item property value
 * @property {string} slotType - The slot type
 * @property {string} slotMode - The slot mode
 * @property {string} font - The font to use
 * @property {string | null} visualisationGroup - The visualisation group ID for matching visData Files
 * @property {File | null} supportingImage - The original / supporting image uploaded file before PhotoEditor processing
 * @property {string | null} supportingImageLineItemPropertyKey - The line item property key for the original image (from admin)
 * @property {{ variantId: number, templateId: string | null, configHandle: string, textByKey: Record<string, string>, imageByKey: Record<string, File | null> } | undefined} [uvInsertSelection] - Full UV insert selection payload for UV_INSERT slots.
 */

/**
 * @typedef {Object} Addon
 * @property {string} addonItemId - The addon item identifier
 * @property {string} variantId - The variant identifier
 * @property {string} stepId - The step identifier
 * @property {number} [index] - Optional index for ordering
 */

/**
 * @typedef {Object} PersonalisationChoice
 * @property {string | File} choice - Component group ID and variant ID combined, or File object for IMAGE_UPLOAD
 * @property {string} slotId - Slot or step ID
 * @property {string} stepId - Step ID
 * @property {number} index - Index for ordering
 * @property {string | undefined} lineItemPropertyKey - The line item property key
 * @property {string | File | undefined} lineItemPropertyKeyValue - The line item property value
 * @property {string} slotType - The slot type
 * @property {string} [font] - The font to use
 * @property {Array<{data: string, type: 'original'}>} [extraImages] - Extra images for IMAGE_UPLOAD slots (original / supporting images)
 * @property {string | null} visualisationGroupId - The visualisation group ID for matching visData Files
 * @property {File | null} supportingImage - The original / supporting image uploaded file before PhotoEditor processing
 * @property {string | null} supportingImageLineItemPropertyKey - The line item property key for the original image (from admin)
 */

/**
 * @typedef {Object} CartItem
 * @property {number} id - The variant ID
 * @property {string} key - The cart item key
 * @property {number} quantity - The quantity to add
 * @property {Object} [properties] - Optional properties for the cart item
 * @property {Array<{c: string, st: string, sl: string, i: number, v?: string, f?: string}>} [properties._personalisation_choices] - Array of personalisation choices
 * @property {Object<string, string>} [properties._personalisation_base_attributes] - Object of base product properties to add
 * @property {string=} [properties._check_id] - The check ID for the cart item
 */

/**
 * @typedef {Object} AddToCartOptions
 * @property {{id: string, quantity: number}} [baseProduct] - The base product to add
 * @property {import('./personalisationData').Slot[]} [slots=[]] - Array of slots to add
 * @property {ChoicePayload[]} [choices=[]] - Array of component groups to add
 * @property {Addon[]} [addons=[]] - Array of addons to add
 * @property {Object} [baseAttributes={}] - Object of base product properties to add
 * @property {string} [endpoint='/cart/add.js'] - Custom endpoint URL
 * @property {Object} [headers={}] - Additional headers for the request
 * @property {'json' | 'html'} [returnType='json'] - Whether to return JSON response
 * @property {string} [country] - The country to add the item to
 * @property {boolean} [autoOpen=true] - Whether to automatically open the cart drawer
 * @property {{url: string, file: File | null, visualiserSlotMode: string, groupId: string, lineItemPropertyKey: string, label: string, positioning: { x: number; y: number; scale: number } | null}[]} [visualiserImageUrls=[]] - The visualiser image URLs and files
 * @property {Object} [selections=null] - The selections object containing slots with croppedImage property
 * @property {string|null} [templateId=null] - The template ID from the personalisation metaobject, sent as a LIP
 */

/**
 * @typedef {Object} AddToCartResponseData
 * @property {string} token - Cart token
 * @property {number} item_count - Number of items in cart
 * @property {Object[]} items - Array of cart items
 * @property {string} [id] - Item ID if single item added
 * @property {number} [quantity] - Item quantity if single item added
 * @property {Object} [properties] - Item properties if single item added
 */

/**
 * @typedef {Object} AddToCartResponseHtml
 * @property {AddToCartResponseData} data - The data from the response
 * @property {string} html - The HTML from the response
 */

/**
 * Adds items to the Shopify cart using the Ajax API
 *
 * @param {AddToCartOptions} options - Configuration options for adding to cart
 * @returns {Promise<void>} Promise that resolves to the cart response
 * @throws {Error} If validation fails or the request fails
 */
async function addToCart({
  baseProduct,
  slots = [],
  choices = [],
  addons = [],
  baseAttributes = {},
  country,
  autoOpen = true,
  visualiserImageUrls = [],
  selections = null,
  templateId = null,
  onRetryStart,
  onRetryEnd
} = {}) {
  let finalHtml = null;
  let finalCartItems = null;
  if (!baseProduct) {
    throw new Error('Base product is required');
  }

  // Input validation
  if (!Array.isArray(choices) || !Array.isArray(addons)) {
    throw new Error('Items must be an array');
  }

  const personalisationChoices = buildPersonalisationChoices({
    choices,
    addons,
    visData: visualiserImageUrls,
    baseProductConfigurationSlotId
  });

  const { formData, stringifiedItems, checkId, featuredImages } = await createPayload({
    choices,
    addons,
    visualiserImageUrls,
    baseProduct,
    country,
    baseAttributes,
    selections,
    baseProductConfigurationSlotId,
    slots,
    templateId
  });

  // Create atc function in closure so we can re-use.
  const addItemToCart = async () =>
    add({
      formData,
      onRetryStart,
      onRetryEnd,
      checkId
    });

  const addingItemsToCart = await new Promise(async (resolve, reject) => {
    // Try to add the items to the cart.
    try {
      // Make the add.js request to add the items to the cart.
      const { response, addedItemIds, cartHtml, cartItems } = await addItemToCart();

      resolve({
        addedItemIds,
        cartHtml,
        cartItems
      });
    } catch (error) {
      // Wait for Shopify to process the request.
      await wait({ ms: 1000 });
      // If it fails - check the cart data and see if the items are already in the cart.
      const cartData = await getCart();
      // Check if there is missing data - e.g. base attributes, personalisation choices, etc.
      const itemsToRemove = await validateAndTryAgain({ cartItems: cartData.items, formData });

      if (itemsToRemove.length > 0) {
        // Items need to be removed and re-added
        await Promise.all(itemsToRemove.map(({ key }) => remove({ key })));
        const { response, addedItemIds, cartHtml, cartItems } = await addItemToCart();
        resolve({ addedItemIds, cartHtml, cartItems });
      } else {
        // Item is already in cart correctly - resolve with cart data
        const addedItem = cartData.items?.find(
          (item) => item.properties?._check_id === formData.get('items[0][properties][_check_id]')
        );
        resolve({
          addedItemIds: addedItem ? [addedItem.id] : [],
          cartHtml: false,
          cartItems: cartData || {}
        });
      }
    }
  });

  const { addedItemIds, cartHtml, cartItems } = addingItemsToCart;
  finalHtml = cartHtml;
  finalCartItems = cartItems;
  try {
    // Check if we have added some images to the cart.
    const containsImageUpload = personalisationChoices.filter((choice) => choice.slotType === 'IMAGE_UPLOAD');
    const containsUvInsertImageUpload = personalisationChoices.some(
      (choice) =>
        choice.slotType === 'UV_INSERT' &&
        'uvInsertImageByKey' in choice &&
        Object.values(choice.uvInsertImageByKey || {}).some((value) => value instanceof File)
    );
    const containsFeaturedImageUrl = cartItems?.items?.some((item) => {
      // Check for _featured_image with or without numbered suffix (_featured_image_1, _featured_image_2, etc).
      return (
        item.properties &&
        Object.keys(item.properties).some((key) => {
          if (!key.startsWith('_featured_image') && !key.endsWith(' Visual')) return false;
          const value = item.properties[key];
          return typeof value === 'string' && value.includes('http');
        })
      );
    });

    if (containsImageUpload.length > 0 || containsUvInsertImageUpload || containsFeaturedImageUrl) {
      // Create the payload for the image switch.
      const newPayloads = finalCartItems?.items
        // Only process items that have been added to the cart.
        .filter((item) => addedItemIds.includes(item.id))
        .map((item) => {
          const { newPayload } = createImageSwitchPayload({
            item
          });
          return newPayload;
        });

      if (newPayloads.length === 0) {
        logError({
          label: 'addToCart',
          error: new Error("Cart item missing - couldn't add image urls"),
          message: "Cart item missing - couldn't add image urls",
          extraData: { newPayloads, addedItemIds, finalCartItems }
        });
        throw new Error("Cart item missing - couldn't add image urls");
      }

      const { cartHtml: changedCartHtml, cartItems: changedCartItems } = await addImageUrls({
        addedItemIds,
        newPayloads,
        cartItems: finalCartItems,
        containsFeaturedImageUrl,
        formData,
        onRetryStart,
        onRetryEnd,
        addItemToCart,
        checkId
      });

      finalHtml = changedCartHtml;
      finalCartItems = changedCartItems;
    }
  } catch (error) {
    // Re-throw validation errors as-is
    if (error.message.includes('Validation errors:')) {
      console.error('[Personalisation addToCart] Validation errors occurred:', error.message);
      logError({
        label: 'addToCart',
        error,
        message: 'Validation errors occurred',
        extraData: { items: stringifiedItems, endpoint: `/cart/add.js`, personalisationChoices }
      });
      throw error;
    }

    console.error('[Personalisation addToCart] Failed to add items to cart:', error.message);
    logError({
      label: 'addToCart',
      error,
      message: 'Failed to add items to cart',
      extraData: { items: stringifiedItems, endpoint: `/cart/add.js` }
    });

    // Wrap other errors with context
    throw new Error(`Failed to add items to cart: ${error.message}`);
  }

  // Clean up any items that have been added but not completed.
  if (featuredImages) {
    const imageKeys = Object.keys(featuredImages);
    // Check for duplicate items in the cart.
    const cartData = await getCart();
    // Find the version that has the images in base
    const matchingItems = cartData.items
      // Get the items that have the checkId in the properties.
      .filter(({ properties }) => properties?._check_id === checkId)
      // Find the version that has all the images moved to base attributes.
      .filter(({ properties }) => Object.keys(properties).some((key) => imageKeys.includes(key)));

    await Promise.all(matchingItems.map(({ key }) => remove({ key })));
    finalCartItems = cartData;
    await window.CartDrawer?.updateCartDrawerContents();
    await window.CartDrawer?.open?.();
  }

  if (finalHtml) {
    await window.CartDrawer?.updateCart?.({ html: finalHtml });
  }

  if (finalCartItems) {
    // Dispatch custom event for gift wrap system to listen to.
    const addToCartEvent = new CustomEvent('PERSONALISATION_ADD_TO_CART_SUCCESS', {
      detail: {
        baseProduct,
        cartData: finalCartItems,
        personalisationChoices,
        baseAttributes
      },
      bubbles: true
    });

    document.dispatchEvent(addToCartEvent);

    // Only open cart drawer if autoOpen is true (default behavior for other journeys)
    if (autoOpen !== false && finalHtml) {
      await window.CartDrawer?.open?.();
      window?.freeGift?.refetch({
        awaitUpdate: false,
        timeout: 500
      });
    }
  } else {
    console.error('[Personalisation addToCart] No data received from cart add request');
  }
}

export { addToCart };
